import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  CameraIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowLeftEndOnRectangleIcon,
  MapPinIcon,
  TruckIcon,
  PhoneIcon,
  TagIcon,
  ArrowPathIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import exifr from 'exifr';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import supabase from './utils/supabaseClient';

// =====================================================================
// /truck — admin captura foto de caminhão na rua, IA extrai placa,
// telefone, tipo, cor, porte. EXIF de geolocalização exibido se houver.
// Salva como lead em leads_caminhoes para outreach posterior.
// =====================================================================

const PORTES = [
  { value: '',         label: '— selecione —' },
  { value: 'pequeno',  label: 'Pequeno (até 1 ton)' },
  { value: 'medio',    label: 'Médio (até 5 ton)' },
  { value: 'grande',   label: 'Grande (acima de 5 ton)' },
];

const TIPOS = [
  { value: '',           label: '— selecione —' },
  { value: 'caminhao',   label: 'Caminhão' },
  { value: 'van',        label: 'Van' },
  { value: 'utilitario', label: 'Utilitário' },
  { value: 'carro',      label: 'Carro' },
  { value: 'moto',       label: 'Moto' },
  { value: 'outro',      label: 'Outro' },
];

const Truck = () => {
  const [authState, setAuthState]   = useState('loading'); // loading | unauth | not_admin | ready
  const [, setSession]              = useState(null);
  const [file, setFile]             = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [exif, setExif]             = useState(null);
  const [uploading, setUploading]   = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [storagePath, setStoragePath] = useState(null);
  const [extracted, setExtracted]   = useState(null);
  const [aiMeta, setAiMeta]         = useState(null);
  const [savedId, setSavedId]       = useState(null);
  const [saving, setSaving]         = useState(false);
  const [endereco, setEndereco]     = useState('');
  const [notas, setNotas]           = useState('');
  const [recent, setRecent]         = useState([]);

  // Auth check
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      setSession(s);
      if (!s) { setAuthState('unauth'); return; }
      const { data } = await supabase.from('admins').select('email').limit(1);
      setAuthState((data && data.length > 0) ? 'ready' : 'not_admin');
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (!s) setAuthState('unauth');
    });
    return () => subscription.unsubscribe();
  }, []);

  // Recent leads
  const loadRecent = useCallback(async () => {
    if (authState !== 'ready') return;
    const { data } = await supabase
      .from('leads_caminhoes')
      .select('id, placa, telefone, tipo_veiculo, status, created_at')
      .order('created_at', { ascending: false })
      .limit(8);
    setRecent(data ?? []);
  }, [authState]);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  // File pick → preview + EXIF
  const handlePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Arquivo precisa ser uma imagem.');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Imagem muito grande (máx 10 MB).');
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setExtracted(null);
    setStoragePath(null);
    setSavedId(null);

    // EXIF client-side
    try {
      const tags = await exifr.parse(f, {
        gps: true, exif: true, ifd0: true, pick: ['DateTimeOriginal', 'Make', 'Model', 'latitude', 'longitude'],
      });
      if (tags) {
        setExif({
          lat: tags.latitude ?? null,
          lng: tags.longitude ?? null,
          taken_at: tags.DateTimeOriginal ?? null,
          dispositivo: tags.Make && tags.Model ? `${tags.Make} ${tags.Model}` : (tags.Model || tags.Make || null),
        });
      } else {
        setExif(null);
      }
    } catch {
      setExif(null);
    }
  };

  // Upload + extract
  const handleAnalyze = async () => {
    if (!file) return;
    setUploading(true);

    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const path = `${new Date().toISOString().slice(0, 7)}/${crypto.randomUUID()}.${ext}`;

    const { error: upErr } = await supabase.storage
      .from('truck-leads')
      .upload(path, file, { contentType: file.type });
    if (upErr) {
      setUploading(false);
      toast.error('Erro no upload: ' + upErr.message);
      return;
    }
    setStoragePath(path);
    setUploading(false);
    setExtracting(true);

    try {
      const r = await fetch('/api/extract-truck-info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storage_path: path, exif }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.error || 'Erro na extração.');
      } else {
        setExtracted(data.extracted);
        setAiMeta(data.ai);
      }
    } catch (e) {
      toast.error('Erro de conexão.');
    } finally {
      setExtracting(false);
    }
  };

  const updateField = (k, v) => setExtracted((e) => ({ ...(e || {}), [k]: v }));

  const handleSave = async () => {
    if (!extracted || !storagePath) return;
    setSaving(true);

    const { data: { user } } = await supabase.auth.getUser();
    const { data: signed } = await supabase.storage
      .from('truck-leads').createSignedUrl(storagePath, 60 * 60 * 24 * 7);

    const { data, error } = await supabase
      .from('leads_caminhoes')
      .insert([{
        storage_path:    storagePath,
        thumbnail_url:   signed?.signedUrl ?? null,
        tipo_veiculo:    extracted.tipo_veiculo || null,
        placa:           extracted.placa ? String(extracted.placa).toUpperCase().replace(/[^A-Z0-9]/g, '') : null,
        cor:             extracted.cor || null,
        porte:           extracted.porte || null,
        caracteristicas: extracted.caracteristicas || null,
        telefone:        extracted.telefone || null,
        texto_extraido:  extracted.texto_extraido || null,
        ai_confidence:   extracted.confidence ?? null,
        exif_lat:        exif?.lat ?? null,
        exif_lng:        exif?.lng ?? null,
        exif_taken_at:   exif?.taken_at ?? null,
        exif_dispositivo: exif?.dispositivo ?? null,
        endereco_aproximado: endereco.trim() || null,
        notas:           notas.trim() || null,
        ai_provider:     aiMeta?.provider ?? null,
        ai_model:        aiMeta?.model ?? null,
        ai_raw_response: extracted,
        created_by:      user?.id ?? null,
      }])
      .select('id')
      .single();
    setSaving(false);
    if (error) {
      console.error(error);
      toast.error('Erro ao salvar lead.');
      return;
    }
    setSavedId(data.id);
    toast.success('Lead salvo!');
    loadRecent();
  };

  const handleReset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setExif(null);
    setExtracted(null);
    setAiMeta(null);
    setStoragePath(null);
    setSavedId(null);
    setEndereco('');
    setNotas('');
  };

  const handleSignOut = () => supabase.auth.signOut();

  if (authState === 'loading') {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-brand-200 border-t-brand-600 animate-spin" />
      </div>
    );
  }
  if (authState === 'unauth') return <Navigate to="/admin" replace />;
  if (authState === 'not_admin') {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
        <div className="card p-8 text-center max-w-sm">
          <p className="font-display font-bold text-ink-900">Acesso restrito</p>
          <p className="text-sm text-ink-500 mt-2">Apenas administradores podem capturar leads.</p>
          <Link to="/" className="mt-4 btn-primary inline-flex">Voltar</Link>
        </div>
      </div>
    );
  }

  const conf = extracted?.confidence ?? 0;
  const confColor =
    conf >= 0.8 ? 'text-green-700' :
    conf >= 0.5 ? 'text-accent-600' : 'text-red-600';

  return (
    <div className="min-h-screen bg-ink-50 pb-24">
      <ToastContainer position="top-right" theme="colored" />

      <header className="sticky top-0 z-40 bg-white border-b border-ink-100 shadow-soft">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between max-w-2xl">
          <div className="flex items-center gap-2">
            <Link to="/" aria-label="Fretz">
              <img src="/fretz-logo-light.png" alt="Fretz" className="h-7 w-auto" />
            </Link>
            <span className="text-ink-200">|</span>
            <span className="text-sm font-semibold text-ink-700">Captura de leads</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin" className="btn-ghost text-xs">Admin</Link>
            <button onClick={handleSignOut} className="p-2 rounded-lg hover:bg-ink-100 text-ink-500" title="Sair">
              <ArrowLeftEndOnRectangleIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 pt-6 max-w-2xl">

        {!file && (
          <label
            htmlFor="truck-file"
            className="block card p-8 text-center cursor-pointer hover:bg-brand-50/40 transition border-2 border-dashed border-ink-200"
          >
            <CameraIcon className="h-10 w-10 text-ink-400 mx-auto" />
            <p className="mt-3 font-display font-bold text-ink-900">Tire ou selecione uma foto</p>
            <p className="text-sm text-ink-500 mt-1">
              Caminhão, van ou utilitário com placa e telefone visíveis.<br />
              Os dados são extraídos automaticamente pela IA.
            </p>
            <input
              id="truck-file"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePick}
              className="sr-only"
            />
          </label>
        )}

        {file && (
          <>
            <div className="card overflow-hidden mb-4">
              <img src={previewUrl} alt="Caminhão" className="w-full max-h-96 object-contain bg-ink-50" />
              <div className="px-4 py-3 border-t border-ink-100 flex items-center justify-between gap-2">
                <p className="text-xs text-ink-500 truncate">{file.name} · {(file.size / 1024).toFixed(0)} KB</p>
                <div className="flex gap-2">
                  <button onClick={handleReset} className="btn-ghost text-xs gap-1">
                    <XMarkIcon className="h-3 w-3" /> Trocar
                  </button>
                  {!extracted && (
                    <button
                      onClick={handleAnalyze}
                      disabled={uploading || extracting}
                      className="btn-primary text-xs gap-1"
                    >
                      {uploading ? 'Enviando…' : extracting ? 'Analisando…' : (<><SparklesIcon className="h-3 w-3" /> Analisar com IA</>)}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* EXIF */}
            {exif && (exif.lat || exif.taken_at || exif.dispositivo) && (
              <div className="card p-4 mb-4 bg-brand-50/30 border-brand-100">
                <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-2">Metadados da foto (EXIF)</p>
                <ul className="text-sm text-ink-700 space-y-1">
                  {exif.lat && exif.lng && (
                    <li className="flex items-center gap-2">
                      <MapPinIcon className="h-4 w-4 text-brand-600" />
                      <a
                        href={`https://www.google.com/maps?q=${exif.lat},${exif.lng}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-brand-700 underline"
                      >
                        {exif.lat.toFixed(5)}, {exif.lng.toFixed(5)} (abrir no Maps)
                      </a>
                    </li>
                  )}
                  {exif.taken_at && (
                    <li>
                      📅 Capturada em {new Date(exif.taken_at).toLocaleString('pt-BR')}
                    </li>
                  )}
                  {exif.dispositivo && <li>📷 {exif.dispositivo}</li>}
                </ul>
              </div>
            )}
          </>
        )}

        {/* Resultado IA */}
        {extracted && (
          <div className="card p-5 space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-display font-bold text-ink-900 flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-brand-600" />
                Dados extraídos
              </p>
              <span className={`text-xs font-semibold ${confColor}`}>
                Confiança {(conf * 100).toFixed(0)}%
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label" htmlFor="ext-tipo">Tipo</label>
                <select id="ext-tipo" className="input"
                        value={extracted.tipo_veiculo || ''}
                        onChange={(e) => updateField('tipo_veiculo', e.target.value || null)}>
                  {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label" htmlFor="ext-porte">Porte</label>
                <select id="ext-porte" className="input"
                        value={extracted.porte || ''}
                        onChange={(e) => updateField('porte', e.target.value || null)}>
                  {PORTES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label flex items-center gap-1" htmlFor="ext-placa">
                  <TagIcon className="h-3.5 w-3.5" /> Placa
                </label>
                <input id="ext-placa" className="input font-mono uppercase tracking-widest"
                       value={extracted.placa || ''}
                       onChange={(e) => updateField('placa', e.target.value)}
                       placeholder="AAA-9999" />
              </div>
              <div>
                <label className="label" htmlFor="ext-cor">Cor</label>
                <input id="ext-cor" className="input"
                       value={extracted.cor || ''}
                       onChange={(e) => updateField('cor', e.target.value)}
                       placeholder="branca, azul…" />
              </div>
              <div className="col-span-2">
                <label className="label flex items-center gap-1" htmlFor="ext-tel">
                  <PhoneIcon className="h-3.5 w-3.5" /> Telefone
                </label>
                <input id="ext-tel" className="input"
                       value={extracted.telefone || ''}
                       onChange={(e) => updateField('telefone', e.target.value)}
                       placeholder="(62) 99999-9999" />
              </div>
              <div className="col-span-2">
                <label className="label" htmlFor="ext-car">Características</label>
                <textarea id="ext-car" className="input min-h-[60px] resize-y"
                          value={extracted.caracteristicas || ''}
                          onChange={(e) => updateField('caracteristicas', e.target.value)}
                          placeholder="Carroceria baú, marca, condição…" />
              </div>
              <div className="col-span-2">
                <label className="label" htmlFor="ext-end">Endereço aproximado</label>
                <input id="ext-end" className="input"
                       value={endereco}
                       onChange={(e) => setEndereco(e.target.value)}
                       placeholder="Onde você viu o caminhão" />
              </div>
              <div className="col-span-2">
                <label className="label" htmlFor="ext-notas">Notas</label>
                <textarea id="ext-notas" className="input min-h-[50px] resize-y"
                          value={notas}
                          onChange={(e) => setNotas(e.target.value)}
                          placeholder="Observações, contexto, etc." />
              </div>
            </div>

            {extracted.texto_extraido && (
              <details className="text-xs">
                <summary className="cursor-pointer text-ink-500">
                  Texto bruto lido pela IA
                </summary>
                <pre className="mt-2 p-3 bg-ink-50 rounded-lg whitespace-pre-wrap font-mono text-[11px]">
                  {extracted.texto_extraido}
                </pre>
              </details>
            )}

            {!savedId ? (
              <button onClick={handleSave} disabled={saving} className="btn-primary w-full gap-2">
                {saving ? 'Salvando…' : (<><CheckCircleIcon className="h-4 w-4" /> Salvar lead</>)}
              </button>
            ) : (
              <div className="text-center p-4 bg-green-50 border border-green-200 rounded-2xl">
                <CheckCircleIcon className="h-8 w-8 text-green-600 mx-auto" />
                <p className="font-semibold text-green-700 mt-2">Lead salvo</p>
                <button onClick={handleReset} className="mt-3 btn-secondary text-xs">
                  <CameraIcon className="h-3 w-3" /> Capturar outro
                </button>
              </div>
            )}

            <p className="text-[11px] text-ink-400 text-center">
              Lead extraído de imagem capturada em via pública. A placa e o
              telefone exibidos no veículo são informações comerciais
              divulgadas publicamente pelo titular.
            </p>
          </div>
        )}

        {/* Recent leads */}
        {!file && recent.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold text-ink-900">Últimos leads</h2>
              <button onClick={loadRecent} className="btn-ghost text-xs gap-1">
                <ArrowPathIcon className="h-3.5 w-3.5" /> Atualizar
              </button>
            </div>
            <div className="card divide-y divide-ink-100">
              {recent.map((l) => (
                <div key={l.id} className="p-4 flex items-center gap-3">
                  <TruckIcon className="h-5 w-5 text-ink-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-ink-900">
                      {l.placa || '— sem placa —'}{l.tipo_veiculo ? ` · ${l.tipo_veiculo}` : ''}
                    </p>
                    <p className="text-xs text-ink-500">{l.telefone || 'sem telefone'} · {new Date(l.created_at).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <span className="badge text-[10px]">{l.status}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-ink-400 text-right mt-2">
              <Link to="/admin" className="underline">Ver todos no painel admin →</Link>
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Truck;
